import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { Lightbox } from '@/components/Lightbox';
import { Expand } from 'lucide-react';

export const Home = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Photo assets
  const logoImage = "https://customer-assets-gfyr7b9c.emergentagent.net/job_bnb-booking-6/artifacts/zk5klw3b_TARGA1.webp";
  const heroImage = "https://customer-assets-gfyr7b9c.emergentagent.net/job_bnb-booking-6/artifacts/i9auzlxm_ChatGPT%20Image%2031%20ago%202026%2C%2012_43_38.png";
  const courtyardImage = "https://customer-assets-gfyr7b9c.emergentagent.net/job_bnb-booking-6/artifacts/vpcvaq9g_IMG_3310.webp";
  const entranceImage = "https://customer-assets-gfyr7b9c.emergentagent.net/job_bnb-booking-6/artifacts/pkz60xsp_IMG_3312.webp";
  const nightImage = "https://customer-assets-gfyr7b9c.emergentagent.net/job_bnb-booking-6/artifacts/knr52rft_IMG_3391.webp";
  const mainDoorImage = "https://customer-assets-gfyr7b9c.emergentagent.net/job_bnb-booking-6/artifacts/5azyo7ep_IMG_3397.webp";
  const detailImage = "https://customer-assets-gfyr7b9c.emergentagent.net/job_bnb-booking-6/artifacts/wcjwbbri_IMG_3402.webp";
  const shadowArt = "https://customer-assets-gfyr7b9c.emergentagent.net/job_bnb-booking-6/artifacts/he0senqr_IMG_3400.webp";
  const wellTerrace = "https://customer-assets-gfyr7b9c.emergentagent.net/job_bnb-booking-6/artifacts/5j2v6jzc_IMG_3403.webp";
  const yellowBalcony = "https://customer-assets-gfyr7b9c.emergentagent.net/job_bnb-booking-6/artifacts/w7ahdpe4_IMG_3405.webp";
  const courtyardYellow = "https://customer-assets-gfyr7b9c.emergentagent.net/job_bnb-booking-6/artifacts/9epx4529_IMG_3411.webp";
  const room03Entrance = "https://customer-assets-gfyr7b9c.emergentagent.net/job_bnb-booking-6/artifacts/2h69kitm_IMG_3414.webp";
  const cactusView = "https://customer-assets-gfyr7b9c.emergentagent.net/job_bnb-booking-6/artifacts/7y9un9kd_IMG_3417.webp";
  const breakfastRoom = "https://customer-assets-gfyr7b9c.emergentagent.net/job_bnb-booking-6/artifacts/7h7q3suy_IMG_3345.webp";
  const breakfastLogo = "https://customer-assets-gfyr7b9c.emergentagent.net/job_bnb-booking-6/artifacts/cinhlxbb_IMG_3347.webp";
  const coffeeStation = "https://customer-assets-gfyr7b9c.emergentagent.net/job_bnb-booking-6/artifacts/f7uwjm1q_IMG_3350.webp";
  const breakfastTable = "https://customer-assets-gfyr7b9c.emergentagent.net/job_bnb-booking-6/artifacts/fmxp6hcn_IMG_3353.webp";

  // Photo galleries per section
  const bnbGallery = [entranceImage, detailImage];
  const courtyardGallery = [courtyardImage, room03Entrance, courtyardYellow, cactusView, shadowArt, mainDoorImage];
  const breakfastGallery = [breakfastTable, breakfastRoom, coffeeStation, breakfastLogo];
  const terraceGallery = [wellTerrace, yellowBalcony, shadowArt];

  // Lightbox state
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const openLightbox = (images, index = 0) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  // Reusable clickable image
  const ClickableImage = ({ src, alt, className, gallery, index, testId }) => (
    <div 
      className={`cursor-pointer group relative ${className}`}
      onClick={() => openLightbox(gallery, index)}
      data-testid={testId || `clickable-image-${alt}`}
    >
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
        <div className="bg-white/90 backdrop-blur-sm rounded-full p-3">
          <Expand size={20} className="text-[#2C302E]" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      {/* Hero Section */}
      <div className="relative h-[95vh] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{backgroundImage: `url(${heroImage})`}}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/70"></div>
        </div>
        
        <div className="relative h-full flex items-center justify-center text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <p 
              className="text-xs sm:text-sm uppercase tracking-[0.3em] text-white/80 mb-6"
              style={{fontFamily: 'Manrope, sans-serif'}}
              data-testid="hero-tagline"
            >
              {t('home.tagline')}
            </p>
            <h1 
              className="text-7xl sm:text-8xl lg:text-9xl text-white mb-6 leading-none"
              style={{fontFamily: '"Dancing Script", cursive', fontWeight: 600}}
              data-testid="hero-title"
            >
              {t('home.title')}
            </h1>
            <p 
              className="text-lg sm:text-xl text-white/90 mb-12 leading-relaxed max-w-2xl mx-auto"
              style={{fontFamily: 'Manrope, sans-serif'}}
            >
              {t('home.subtitle')}
            </p>
            <Button
              data-testid="book-now-button"
              onClick={() => navigate('/rooms')}
              size="lg"
              className="bg-[#8A9A86] hover:bg-[#7A8A76] text-white px-12 py-6 rounded-full text-lg transition-transform hover:scale-105"
              style={{fontFamily: 'Manrope, sans-serif'}}
            >
              {t('home.bookNow')}
            </Button>
          </motion.div>
        </div>
      </div>

      {/* Our B&B Section */}
      <div className="py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-xs uppercase tracking-[0.3em] text-[#8A9A86] mb-4" style={{fontFamily: 'Manrope, sans-serif'}}>
              {t('home.ourBnb')}
            </p>
            <h2 className="text-5xl sm:text-6xl text-[#8A9A86] mb-6 leading-none" style={{fontFamily: '"Dancing Script", cursive', fontWeight: 600}}>
              Curtigghiu
            </h2>
            <p className="text-lg text-[#2C302E]/70 leading-relaxed mb-8" style={{fontFamily: 'Manrope, sans-serif'}}>
              {t('home.ourBnbDesc')}
            </p>
            <Button
              data-testid="view-rooms-button"
              onClick={() => navigate('/rooms')}
              variant="outline"
              className="border-2 border-[#8A9A86] text-[#8A9A86] hover:bg-[#8A9A86] hover:text-white px-8 py-6 rounded-full transition-all"
            >
              {t('home.viewRooms')}
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative h-[500px]"
          >
            <div className="absolute top-0 right-0 w-3/4 h-3/4 shadow-[0_20px_50px_rgb(0,0,0,0.15)] rounded-lg overflow-hidden">
              <ClickableImage
                src={entranceImage}
                alt="Curtigghiu entrance"
                gallery={bnbGallery}
                index={0}
                className="w-full h-full rounded-lg overflow-hidden"
              />
            </div>
            <div className="absolute bottom-0 left-0 w-2/3 h-2/3 shadow-[0_20px_50px_rgb(0,0,0,0.15)] rounded-lg overflow-hidden border-8 border-[#FAFAF7]">
              <ClickableImage
                src={detailImage}
                alt="Curtigghiu detail"
                gallery={bnbGallery}
                index={1}
                className="w-full h-full"
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Courtyard Gallery - Bento Grid */}
      <div className="py-24 px-4 sm:px-6 lg:px-8 bg-[#F0F2EB]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-[#8A9A86] mb-4" style={{fontFamily: 'Manrope, sans-serif'}}>
              {t('home.courtyard')}
            </p>
            <h2 className="text-4xl sm:text-5xl font-light tracking-tight text-[#2C302E] mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              {t('home.courtyardTitle')}
            </h2>
            <p className="text-lg text-[#2C302E]/70 max-w-2xl mx-auto" style={{fontFamily: 'Manrope, sans-serif'}}>
              {t('home.courtyardDesc')}
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-4 grid-rows-3 gap-4 h-[700px]"
          >
            <ClickableImage src={courtyardImage} alt="Courtyard" gallery={courtyardGallery} index={0} testId="clickable-image-courtyard-main" className="col-span-2 row-span-2 rounded-lg overflow-hidden h-full w-full" />
            <ClickableImage src={room03Entrance} alt="Room 03 entrance" gallery={courtyardGallery} index={1} testId="clickable-image-courtyard-room03" className="col-span-2 row-span-1 rounded-lg overflow-hidden h-full w-full" />
            <ClickableImage src={courtyardYellow} alt="Yellow table courtyard" gallery={courtyardGallery} index={2} testId="clickable-image-courtyard-yellow" className="col-span-1 row-span-1 rounded-lg overflow-hidden h-full w-full" />
            <ClickableImage src={cactusView} alt="Cactus view" gallery={courtyardGallery} index={3} testId="clickable-image-courtyard-cactus" className="col-span-1 row-span-1 rounded-lg overflow-hidden h-full w-full" />
            <ClickableImage src={shadowArt} alt="Shadow decoration" gallery={courtyardGallery} index={4} testId="clickable-image-courtyard-shadow" className="col-span-2 row-span-1 rounded-lg overflow-hidden h-full w-full" />
            <ClickableImage src={mainDoorImage} alt="Main historic door" gallery={courtyardGallery} index={5} testId="clickable-image-courtyard-door" className="col-span-2 row-span-1 rounded-lg overflow-hidden h-full w-full" />
          </motion.div>
        </div>
      </div>

      {/* Breakfast Room Section */}
      <div className="py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-xs uppercase tracking-[0.3em] text-[#8A9A86] mb-4" style={{fontFamily: 'Manrope, sans-serif'}}>
                {t('home.breakfast')}
              </p>
              <h2 className="text-4xl sm:text-5xl font-light tracking-tight text-[#2C302E] mb-6" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                {t('home.breakfastTitle')}
              </h2>
              <p className="text-lg text-[#2C302E]/70 leading-relaxed mb-6" style={{fontFamily: 'Manrope, sans-serif'}}>
                {t('home.breakfastDesc')}
              </p>
              <ClickableImage
                src={breakfastTable}
                alt="Breakfast table"
                gallery={breakfastGallery}
                index={0}
                className="rounded-lg overflow-hidden h-64"
              />
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-3 grid grid-cols-2 grid-rows-2 gap-3 h-[600px]"
          >
            <ClickableImage src={breakfastRoom} alt="Breakfast common room" gallery={breakfastGallery} index={1} className="col-span-2 rounded-lg overflow-hidden w-full h-full" />
            <ClickableImage src={coffeeStation} alt="Coffee station" gallery={breakfastGallery} index={2} className="rounded-lg overflow-hidden w-full h-full" />
            <ClickableImage src={breakfastLogo} alt="Room with Curtigghiu logo" gallery={breakfastGallery} index={3} className="rounded-lg overflow-hidden w-full h-full" />
          </motion.div>
        </div>
      </div>

      {/* Terrace Section - Panoramic View */}
      <div className="py-24 px-4 sm:px-6 lg:px-8 bg-[#F0F2EB]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-[#8A9A86] mb-4" style={{fontFamily: 'Manrope, sans-serif'}}>
              {t('home.terrace')}
            </p>
            <h2 className="text-4xl sm:text-5xl font-light tracking-tight text-[#2C302E] mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              {t('home.terraceTitle')}
            </h2>
            <p className="text-lg text-[#2C302E]/70 max-w-2xl mx-auto" style={{fontFamily: 'Manrope, sans-serif'}}>
              {t('home.terraceDesc')}
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-4"
          >
            <ClickableImage src={wellTerrace} alt="Well terrace panoramic" gallery={terraceGallery} index={0} testId="clickable-image-terrace-well" className="md:col-span-2 rounded-lg overflow-hidden aspect-[4/3]" />
            <div className="grid grid-rows-2 gap-4">
              <ClickableImage src={yellowBalcony} alt="Yellow balcony" gallery={terraceGallery} index={1} testId="clickable-image-terrace-balcony" className="rounded-lg overflow-hidden aspect-[4/3]" />
              <ClickableImage src={shadowArt} alt="Shadow decoration" gallery={terraceGallery} index={2} testId="clickable-image-terrace-shadow" className="rounded-lg overflow-hidden aspect-[4/3]" />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Night atmosphere */}
      <div className="relative h-[70vh] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{backgroundImage: `url(${nightImage})`}}
        >
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
        <div className="relative h-full flex items-center justify-center text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <p className="text-xs uppercase tracking-[0.3em] text-white/80 mb-6" style={{fontFamily: 'Manrope, sans-serif'}}>
              {t('home.atmosphere')}
            </p>
            <h2 className="text-5xl sm:text-6xl font-light tracking-tight text-white mb-8" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              {t('home.atmosphereTitle')}
            </h2>
            <Button
              data-testid="view-all-rooms-button"
              onClick={() => navigate('/rooms')}
              size="lg"
              className="bg-white text-[#2C302E] hover:bg-[#F0F2EB] px-12 py-6 rounded-full text-lg transition-transform hover:scale-105"
            >
              {t('home.bookNow')}
            </Button>
          </motion.div>
        </div>
      </div>

      {/* Lightbox */}
      <Lightbox
        images={lightboxImages}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        alt="Curtigghiu"
      />
    </div>
  );
};
